import {
  convertFfs,
  FFS_DISPLAY_DECIMALS,
  WORKING_UNIT_PRESSURE,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { DescribedTerm } from 'features/libra/components/DescribedTerm'
import { ReactNode } from 'react'
import { GeckoMeasurementUnit } from 'types'

export const fmt = (
  n: number | null,
  precision = FFS_DISPLAY_DECIMALS
): string => (n === null ? '--' : n.toFixed(precision))

// Convert a canonical working-unit scalar to a display unit. The formula panel
// substitutes and displays values in the same unit as the computed result, so a
// user checking the arithmetic sees a dimensionally consistent equation.
const toDisplay = (
  n: number | null,
  from: GeckoMeasurementUnit,
  to: GeckoMeasurementUnit
): number | null => (n === null ? null : convertFfs(n, from, to))

/**
 * Plain-language descriptions of every variable on the Minimum Thickness step,
 * surfaced on hover (see `DescribedTerm`). Single source of truth for this copy
 * so inputs, calculated outputs, and the observed-check panel stay consistent.
 *
 * Wording for the engineering definitions (notably `Sy` and `Y`) should be
 * confirmed by an FFS / piping SME.
 */
export const FFS_MIN_THICKNESS_DESCRIPTIONS = {
  // Inputs
  D: 'Outer diameter of the pipe section (radius x 2 from the asset-model entry).',
  tNom: 'Nominal (as-designed, undamaged) wall thickness of the component.',
  P: 'Internal design pressure the component is rated for.',
  Sy: 'Allowable stress for the material at temperature (S in the API 579 equations), sourced from yield strength.',
  E: 'Weld joint (quality) factor, 0-1, accounting for longitudinal weld strength.',
  Y: 'Temperature-dependent coefficient in the thick-wall pressure-design equations (ASME B31 / API 579).',
  W: 'Weld joint strength reduction factor (ASME B31.3), 0-1, applied to the allowable stress term.',
  // Calculated: minimum thickness
  tMinC:
    'Minimum wall thickness needed to resist circumferential (hoop) stress at design pressure.',
  tMinL: 'Minimum wall thickness needed to resist longitudinal stress.',
  tMin: 'Governing required thickness -- the larger of the circumferential and longitudinal values.',
  // Calculated: re-rated MAWP (at measured minimum thickness)
  mawpRC:
    'Maximum allowable working pressure limited by circumferential (hoop) stress, re-rated at the minimum measured remaining thickness.',
  mawpRL:
    'Maximum allowable working pressure limited by longitudinal stress, re-rated at the minimum measured remaining thickness.',
  mawpR:
    'Governing re-rated MAWP -- the smaller (most limiting) of the circumferential and longitudinal values at measured minimum thickness.',
  // Observed check
  requiredTMin:
    'The governing minimum required thickness the component must retain.',
  observedMin:
    'Smallest measured remaining thickness across the inspection dataset (thickness CDF).',
} as const

export interface MinThicknessRawInputs {
  P_MPa: number | null
  D_mm: number | null
  Sy_MPa: number | null
  E: number | null
  Y: number | null
  W_ratio: number | null
  tNom_mm: number | null
  tMeas_mm: number | null
}

export interface MinThicknessFormulaJsx {
  /** Symbolic + substituted JSX for each formula in the Min Thickness step. */
  tMinC: { symbolic: ReactNode; substituted: ReactNode }
  tMinL: { symbolic: ReactNode; substituted: ReactNode }
  mawpRC: { symbolic: ReactNode; substituted: ReactNode }
  mawpRL: { symbolic: ReactNode; substituted: ReactNode }
}

/**
 * Builds the symbolic + substituted JSX for every formula on the Min
 * Thickness step. Each substituted cell hovers to show input provenance.
 *
 * Kept in its own file to keep the step component focused on data wiring
 * and validation merging.
 */
export function buildMinThicknessFormulaJsx(
  raw: MinThicknessRawInputs,
  thicknessUnit: GeckoMeasurementUnit = WORKING_UNIT_THICKNESS,
  pressureUnit: GeckoMeasurementUnit = WORKING_UNIT_PRESSURE
): MinThicknessFormulaJsx {
  // Substituted values are shown in the active display units (E and Y are
  // dimensionless). The API 579 equations are dimensionally consistent, so the
  // substituted arithmetic matches the display-unit result.
  const Pdisp = toDisplay(raw.P_MPa, WORKING_UNIT_PRESSURE, pressureUnit)
  const Ddisp = toDisplay(raw.D_mm, WORKING_UNIT_THICKNESS, thicknessUnit)
  const Sydisp = toDisplay(raw.Sy_MPa, WORKING_UNIT_PRESSURE, pressureUnit)
  const tMeasDisp = toDisplay(
    raw.tMeas_mm,
    WORKING_UNIT_THICKNESS,
    thicknessUnit
  )

  const Pcell = (
    <DescribedTerm description='from Asset Model -- Design Pressure (P)'>
      {fmt(Pdisp)}
    </DescribedTerm>
  )
  const Dcell = (
    <DescribedTerm description='from Asset Model -- Outer Diameter (D = radius * 2)'>
      {fmt(Ddisp)}
    </DescribedTerm>
  )
  const Sycell = (
    <DescribedTerm description='from Asset Model -- Allowable Stress (S)'>
      {fmt(Sydisp)}
    </DescribedTerm>
  )
  const Ecell = (
    <DescribedTerm description='from Asset Model -- Joint Efficiency (E)'>
      {fmt(raw.E)}
    </DescribedTerm>
  )
  const Ycell = (
    <DescribedTerm description='from Asset Model -- Pressure Coefficient (Y)'>
      {fmt(raw.Y)}
    </DescribedTerm>
  )
  const Wcell = (
    <DescribedTerm description='from Asset Model -- Weld Strength Reduction Factor (W)'>
      {fmt(raw.W_ratio)}
    </DescribedTerm>
  )
  const tMeasCell = (
    <DescribedTerm description='from inspection -- minimum measured remaining thickness (t_mm), from the thickness CDF'>
      {fmt(tMeasDisp)}
    </DescribedTerm>
  )

  return {
    tMinC: {
      symbolic: (
        <>
          t<sub>min</sub>
          <sup>C</sup> = (P * D) / (2 * S * E * W + 2 * P * Y)
        </>
      ),
      substituted: (
        <>
          ({Pcell} * {Dcell}) / (2 * {Sycell} * {Ecell} * {Wcell} + 2 * {Pcell}{' '}
          * {Ycell})
        </>
      ),
    },
    tMinL: {
      symbolic: (
        <>
          t<sub>min</sub>
          <sup>L</sup> = (P * D) / (4 * S * E * W + 4 * P * Y)
        </>
      ),
      substituted: (
        <>
          ({Pcell} * {Dcell}) / (4 * {Sycell} * {Ecell} * {Wcell} + 4 * {Pcell}{' '}
          * {Ycell})
        </>
      ),
    },
    mawpRC: {
      symbolic: (
        <>
          MAWP<sub>r</sub>
          <sup>C</sup> = (2 * S * E * W * t<sub>mm</sub>) / (D - 2 * Y * t
          <sub>mm</sub>)
        </>
      ),
      substituted: (
        <>
          (2 * {Sycell} * {Ecell} * {Wcell} * {tMeasCell}) / ({Dcell} - 2 *{' '}
          {Ycell} * {tMeasCell})
        </>
      ),
    },
    mawpRL: {
      symbolic: (
        <>
          MAWP<sub>r</sub>
          <sup>L</sup> = (4 * S * E * W * t<sub>mm</sub>) / (D - 4 * Y * t
          <sub>mm</sub>)
        </>
      ),
      substituted: (
        <>
          (4 * {Sycell} * {Ecell} * {Wcell} * {tMeasCell}) / ({Dcell} - 4 *{' '}
          {Ycell} * {tMeasCell})
        </>
      ),
    },
  }
}
